import React, { useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageLayout } from '@/components/ui/page-layout';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sparkles,
  Star,
  Moon,
  Sun,
  Heart,
  RefreshCw,
  ArrowRight,
  MessageCircle,
  Send
} from 'lucide-react';

interface GameCard {
  id: number;
  title: string;
  description: string;
  message: string;
  symbol: React.ReactNode;
  color: string;
  gradient: string;
}

const cards: GameCard[] = [
  {
    id: 1,
    title: "Porte 1 - L'Expression Créative",
    description: "La porte de l'inspiration divine",
    message: "Votre créativité cherche à s'exprimer. Écoutez vos inspirations, elles sont précieuses.",
    symbol: <Star className="w-12 h-12" />,
    color: "from-amber-500/90 to-orange-600/90",
    gradient: "from-amber-100 via-orange-50 to-amber-100"
  },
  {
    id: 2,
    title: "Porte 2 - Le Calme Intérieur",
    description: "La porte de la réceptivité",
    message: "Le silence est votre allié aujourd'hui. Dans le calme, vous trouverez vos réponses.",
    symbol: <Moon className="w-12 h-12" />,
    color: "from-indigo-500/90 to-blue-600/90",
    gradient: "from-indigo-100 via-blue-50 to-indigo-100"
  },
  {
    id: 3,
    title: "Porte 3 - L'Alignement",
    description: "La porte de la clarté",
    message: "Tout s'aligne parfaitement. Faites confiance au timing de la vie.",
    symbol: <Sun className="w-12 h-12" />,
    color: "from-yellow-500/90 to-orange-600/90",
    gradient: "from-yellow-100 via-orange-50 to-yellow-100"
  },
  {
    id: 4,
    title: "Porte 4 - L'Amour Inconditionnel",
    description: "La porte du cœur",
    message: "Ouvrez votre cœur à la vie. L'amour est la réponse que vous cherchez.",
    symbol: <Heart className="w-12 h-12" />,
    color: "from-pink-500/90 to-rose-600/90",
    gradient: "from-pink-100 via-rose-50 to-pink-100"
  }
];

export default function TirageCartes() {
  const [selectedCard, setSelectedCard] = useState<GameCard | null>(null);
  const [isRevealed, setIsRevealed] = useState(false);
  const [isFlipping, setIsFlipping] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    message: ''
  });

  const cardSectionRef = useRef<HTMLDivElement>(null);

  const drawCard = () => {
    setIsFlipping(true);
    const randomCard = cards[Math.floor(Math.random() * cards.length)];
    setSelectedCard(randomCard);
    
    setTimeout(() => {
      cardSectionRef.current?.scrollIntoView({ 
        behavior: 'smooth',
        block: 'center'
      });
      setIsRevealed(true);
      setIsFlipping(false);
    }, 1000);
  };

  const resetCards = () => {
    setSelectedCard(null);
    setIsRevealed(false);
  };

  return (
    <PageLayout showBackButton={false}>
      {/* Hero Section */}
      <div className="relative min-h-[80vh] flex items-center justify-center overflow-hidden px-4">
        {/* Background Image */}
        <div 
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1519834785169-98be25ec3f84?q=80&w=3648&auto=format&fit=crop')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'brightness(0.3)'
          }}
        />

        {/* Gradient Overlay */}
        <div 
          className="absolute inset-0 z-1"
          style={{
            background: 'linear-gradient(to bottom, rgba(84, 7, 91, 0.9), rgba(147, 51, 234, 0.7))'
          }}
        />

        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="space-y-8"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="mb-12"
            >
              <div className="p-4 bg-white/10 backdrop-blur-sm rounded-2xl inline-block">
                <Sparkles className="w-12 h-12 sm:w-16 sm:h-16 text-white" />
              </div>
            </motion.div>

            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Tirage de Carte HED
            </h1>
            
            <p className="text-lg sm:text-xl md:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              Recevez un message guidant pour votre journée
            </p>

            {!selectedCard && (
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
                <Button 
                  onClick={drawCard}
                  className="w-full sm:w-auto bg-white text-purple-900 hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Tirer une carte
                  <Star className="w-5 h-5 ml-2" />
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      </div>

      {/* Card Drawing Section */}
      <div 
        ref={cardSectionRef}
        className="relative z-10 flex flex-col items-center px-4 py-20 scroll-mt-8"
      >
        {selectedCard && (
          <div className="w-full max-w-sm mx-auto flex flex-col items-center">
            <AnimatePresence mode="wait">
              {isRevealed ? (
                <motion.div
                  key="card-revealed"
                  initial={{ opacity: 0, rotateY: -180 }}
                  animate={{ opacity: 1, rotateY: 0 }}
                  exit={{ opacity: 0, rotateY: 180 }}
                  transition={{ duration: 0.6 }}
                  className="w-full"
                >
                  <div className="aspect-[9/16] w-full rounded-3xl overflow-hidden shadow-xl">
                    <div className={`relative w-full h-full bg-gradient-to-br ${selectedCard.gradient}`}>
                      <div className={`absolute inset-0 bg-gradient-to-b ${selectedCard.color}`} />
                      
                      <div className="relative h-full flex flex-col items-center justify-center p-6 text-white text-center">
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            delay: 0.3,
                            duration: 0.5,
                            type: "spring",
                            stiffness: 200
                          }}
                          className="mb-6"
                        >
                          <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm mx-auto">
                            <motion.div
                              animate={{ 
                                scale: [1, 1.2, 1],
                                rotate: [0, 10, -10, 0]
                              }}
                              transition={{ 
                                duration: 3,
                                repeat: Infinity,
                                ease: "easeInOut"
                              }}
                            >
                              {selectedCard.symbol}
                            </motion.div>
                          </div>
                        </motion.div>
                        
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                          className="space-y-4"
                        >
                          <motion.h2 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            className="text-2xl font-bold mb-2"
                          >
                            {selectedCard.title}
                          </motion.h2>
                          <motion.p 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 }}
                            className="text-white/90 text-lg"
                          >
                            {selectedCard.description}
                          </motion.p>
                          <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1 }}
                            className="bg-white/20 p-6 rounded-xl backdrop-blur-sm mt-6"
                          >
                            <p className="text-xl font-medium leading-relaxed">
                              {selectedCard.message}
                            </p>
                          </motion.div>
                        </motion.div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 space-y-4">
                    <Button
                      onClick={resetCards}
                      variant="outline"
                      className="w-full gap-2 rounded-xl"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Tirer une autre carte
                    </Button>

                    <Button
                      className="w-full bg-[#54075B] hover:bg-[#54075B]/90 gap-2 rounded-xl"
                      onClick={() => window.location.href = '/lecture-design'}
                    >
                      Recevoir une lecture complète
                      <ArrowRight className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="ghost"
                      className="w-full gap-2 rounded-xl"
                      onClick={() => window.location.href = '/forum'}
                    >
                      Partager mon tirage sur le forum
                      <MessageCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="card-back"
                  initial={{ opacity: 0, rotateY: 180 }}
                  animate={{ opacity: 1, rotateY: isFlipping ? -180 : 0 }}
                  exit={{ opacity: 0, rotateY: -180 }}
                  transition={{ duration: 0.6 }}
                  className="aspect-[9/16] w-full rounded-3xl bg-gradient-to-br from-purple-600 to-pink-600 p-1 shadow-xl"
                >
                  <div className="w-full h-full rounded-[calc(1.5rem-1px)] bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center p-8">
                    <div className="text-center text-white">
                      <motion.div
                        animate={{ 
                          scale: [1, 1.2, 1],
                          rotate: [0, 360],
                        }}
                        transition={{ 
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <Sparkles className="w-12 h-12 mx-auto mb-4" />
                      </motion.div>
                      <p className="text-lg font-medium">Votre message se révèle...</p>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Contact Form */}
        <AnimatePresence>
          {showContactForm && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full max-w-md mx-auto mt-8"
            >
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Partagez votre ressenti</h3>
                  <form className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nom
                      </label>
                      <input
                        type="text"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Votre message
                      </label>
                      <textarea
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        rows={4}
                        placeholder="Partagez votre ressenti sur le tirage..."
                      />
                    </div>
                    <Button className="w-full bg-[#54075B] hover:bg-[#54075B]/90 shadow-lg hover:shadow-xl transition-all duration-300">
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </PageLayout>
  );
}